/**
 * EventBus — bridge between the SVG game world and React components.
 * (Formerly Phaser.Events.EventEmitter; replaced with a tiny local emitter
 * so the 1.2 MB Phaser dependency doesn't ship for one class.)
 */
type Listener = (...args: never[]) => void

class Emitter {
  private listeners = new Map<string, Set<Listener>>()

  on(event: string, fn: Listener): this {
    let set = this.listeners.get(event)
    if (!set) { set = new Set(); this.listeners.set(event, set) }
    set.add(fn)
    return this
  }

  off(event: string, fn?: Listener): this {
    if (!fn) { this.listeners.delete(event); return this }
    this.listeners.get(event)?.delete(fn)
    return this
  }

  emit(event: string, ...args: unknown[]): boolean {
    const set = this.listeners.get(event)
    if (!set || set.size === 0) return false
    // Copy so listeners that add/remove during emit don't affect this dispatch
    for (const fn of [...set]) (fn as (...a: unknown[]) => void)(...args)
    return true
  }
}

export const EventBus = new Emitter()

// ── Event name constants ──────────────────────────────────────────────────────
export const EVENTS = {
  // Phaser → React
  SCENE_READY:        'scene-ready',
  BOSS_NODE_CLICK:    'boss-node-click',    // { standardId, bossName }
  BATTLE_START:       'battle-start',       // { standardId }
  BATTLE_BOSS_HIT:    'battle-boss-hit',    // { damage, bossHp, bossMaxHp }
  BATTLE_PLAYER_HIT:  'battle-player-hit',  // { damage, playerHp }
  BATTLE_VICTORY:     'battle-victory',     // { standardId, xpEarned }
  BATTLE_DEFEAT:      'battle-defeat',      // { standardId }
  PLAYER_MOVED:       'player-moved',       // { x, y }

  // React → Phaser
  ANSWER_CORRECT:     'answer-correct',     // { damage: number }
  ANSWER_WRONG:       'answer-wrong',
  BATTLE_COMPLETE:    'battle-complete',    // { victory: boolean, xp: number }
  NAVIGATE_TO_BOSS:   'navigate-to-boss',   // { standardId }
  AVATAR_UPDATED:     'avatar-updated',     // { skin, hair, outfit } (Phaser hex numbers)
  TEXT_SCALE_UPDATED: 'text-scale-updated', // { scale: number }
} as const

export type EventName = (typeof EVENTS)[keyof typeof EVENTS]
